import { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { projects } from "../data/portfolio";

const easeOut = [0.22, 1, 0.36, 1];

const ProjectCard = ({ project, index, onOpen, onOpenViewer }) => {
  const hasViewer = !!project.caseStudyImages?.length;
  const hasExternal = !!project.caseStudyUrl;

  const Media = () => (
    <div
      className={`relative overflow-hidden bg-[#eaeae3] ${project.height}`}
    >
      <img
        src={project.cover}
        alt={project.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
      />
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between text-xs font-bold tracking-[0.18em] uppercase text-white mix-blend-difference pointer-events-none">
        <span>{String(index + 1).padStart(2, "0")} / Case Study</span>
      </div>
      <div className="absolute bottom-4 right-4 inline-flex h-10 w-10 items-center justify-center bg-black text-white text-sm transition-all duration-300 group-hover:bg-[#FF3B30] group-hover:rotate-45 pointer-events-none">
        ↗
      </div>
    </div>
  );

  const Meta = () => (
    <>
      <div className="mt-5 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h3 className="text-2xl lg:text-3xl font-black tracking-tighter leading-tight">
            {project.title}
          </h3>
          <p className="mt-1 text-sm font-light text-black/60">
            {project.client} · {project.role}
          </p>
        </div>
        <div className="hidden sm:flex flex-wrap items-center gap-2 shrink-0 justify-end">
          {project.tags.map((t) => (
            <span
              key={t}
              className="text-[10px] font-bold tracking-[0.15em] uppercase border border-black/20 px-2 py-1"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <p className="mt-3 text-sm lg:text-base font-light text-black/70 max-w-xl leading-relaxed">
        {project.summary}
      </p>
    </>
  );

  const cardCommon = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, delay: index * 0.08, ease: easeOut },
    className: `group ${project.span} col-span-12`,
    "data-testid": `project-card-${project.id}`,
  };

  // Internal-modal projects: whole card is one button
  if (!hasExternal && !hasViewer) {
    return (
      <motion.button
        {...cardCommon}
        type="button"
        onClick={() => onOpen(project)}
        className={`${cardCommon.className} text-left block w-full`}
        aria-label={`Open ${project.title} case study`}
      >
        <Media />
        <Meta />
      </motion.button>
    );
  }

  // Inline-viewer projects: card opens an in-page case-study viewer
  if (hasViewer) {
    return (
      <motion.div {...cardCommon}>
        <button
          type="button"
          onClick={() => onOpenViewer(project)}
          aria-label={`Open ${project.title} case study viewer`}
          data-testid={`project-card-${project.id}-case-study`}
          className="block w-full text-left"
        >
          <Media />
          <Meta />
        </button>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-bold tracking-[0.18em] uppercase">
          <button
            type="button"
            onClick={() => onOpenViewer(project)}
            className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
            data-testid={`project-${project.id}-view-case-study`}
          >
            View case study
            <span aria-hidden>→</span>
          </button>

          {project.prototypeUrl && (
            <a
              href={project.prototypeUrl}
              target="_blank"
              rel="noopener"
              referrerPolicy="strict-origin-when-cross-origin"
              className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
              data-testid={`project-${project.id}-prototype`}
            >
              Figma prototype
              <span aria-hidden>↗</span>
            </a>
          )}

          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener"
              referrerPolicy="strict-origin-when-cross-origin"
              className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
              data-testid={`project-${project.id}-live`}
            >
              Live website
              <span aria-hidden>↗</span>
            </a>
          )}
        </div>
      </motion.div>
    );
  }

  // External-link projects: media + title link to case study, prototype link is separate
  return (
    <motion.div {...cardCommon}>
      <a
        href={project.caseStudyUrl}
        target="_blank"
        rel="noopener"
        referrerPolicy="strict-origin-when-cross-origin"
        aria-label={`Open ${project.title} case study in a new tab`}
        data-testid={`project-card-${project.id}-case-study`}
        className="block"
      >
        <Media />
        <Meta />
      </a>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-bold tracking-[0.18em] uppercase">
        <a
          href={project.caseStudyUrl}
          target="_blank"
          rel="noopener"
          referrerPolicy="strict-origin-when-cross-origin"
          className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
          data-testid={`project-${project.id}-view-case-study`}
        >
          View case study
          <span aria-hidden>↗</span>
        </a>

        {project.prototypeUrl && (
          <a
            href={project.prototypeUrl}
            target="_blank"
            rel="noopener"
            referrerPolicy="strict-origin-when-cross-origin"
            className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
            data-testid={`project-${project.id}-prototype`}
          >
            Figma prototype
            <span aria-hidden>↗</span>
          </a>
        )}

        {project.liveUrl && (
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener"
            referrerPolicy="strict-origin-when-cross-origin"
            className="inline-flex items-center gap-1.5 text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
            data-testid={`project-${project.id}-live`}
          >
            Live website
            <span aria-hidden>↗</span>
          </a>
        )}
      </div>
    </motion.div>
  );
};

export const Projects = () => {
  const [active, setActive] = useState(null);
  const [viewer, setViewer] = useState(null);
  const isOpen = !!active;
  const isViewerOpen = !!viewer;

  return (
    <section
      id="work"
      className="py-24 lg:py-32 border-t border-black/10"
      data-testid="projects-section"
    >
      <div className="container-portfolio">
        <div className="grid grid-cols-12 gap-6 mb-14 lg:mb-20 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/50 mb-4">
              §01 — Selected Work
            </p>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.9]">
              Recent case
              <br />
              studies, picked
              <br />
              by hand.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-3 lg:col-start-10">
            <p className="text-sm font-light text-black/70 leading-relaxed">
              A small slice of the work — chosen because the problems were
              honest, the constraints real, and the outcomes measurable.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {projects.map((p, i) => (
            <ProjectCard
              key={p.id}
              project={p}
              index={i}
              onOpen={setActive}
              onOpenViewer={setViewer}
            />
          ))}
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto bg-[#f4f4f0] border-black/10 rounded-none p-0"
          data-testid="project-modal"
        >
          {active && (
            <div>
              <div className="relative h-[300px] sm:h-[420px] w-full overflow-hidden bg-[#eaeae3]">
                <img
                  src={active.cover}
                  alt={active.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute top-4 left-4 right-4 flex justify-between text-[11px] font-bold tracking-[0.2em] uppercase text-white mix-blend-difference">
                  <span>Case Study</span>
                  <span>{active.year}</span>
                </div>
              </div>
              <div className="px-6 sm:px-12 py-10">
                <DialogHeader className="space-y-3 text-left">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF3B30]">
                    {active.client} · {active.role}
                  </p>
                  <DialogTitle className="text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className="text-base sm:text-lg font-light text-black/80 leading-relaxed max-w-2xl pt-2">
                    {active.overview}
                  </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-10 pt-10 border-t border-black/10">
                  {active.metrics.map((m) => (
                    <div key={m.label}>
                      <div className="text-3xl sm:text-4xl font-black tracking-tighter">
                        {m.value}
                      </div>
                      <div className="mt-1 text-[11px] tracking-[0.18em] uppercase font-bold text-black/55">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 pt-8 border-t border-black/10 flex flex-wrap gap-2">
                  {active.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] font-bold tracking-[0.18em] uppercase border border-black/30 px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* In-page case-study image viewer (used by FairMate etc.) */}
      <Dialog open={isViewerOpen} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent
          className="max-w-4xl w-[96vw] max-h-[94vh] overflow-y-auto bg-[#f4f4f0] border-black/10 rounded-none p-0"
          data-testid="case-study-viewer"
        >
          {viewer && (
            <div>
              <DialogHeader className="px-6 sm:px-10 pt-8 pb-6 text-left sticky top-0 bg-[#f4f4f0]/95 backdrop-blur z-10 border-b border-black/10">
                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#FF3B30]">
                  {viewer.client} · {viewer.year}
                </p>
                <DialogTitle className="text-3xl sm:text-4xl font-black tracking-tighter leading-[0.95] mt-1">
                  {viewer.title}
                </DialogTitle>
                <DialogDescription className="text-sm font-light text-black/70 leading-relaxed pt-2 max-w-2xl">
                  {viewer.summary}
                </DialogDescription>
                {viewer.prototypeUrl && (
                  <div className="pt-3">
                    <a
                      href={viewer.prototypeUrl}
                      target="_blank"
                      rel="noopener"
                      referrerPolicy="strict-origin-when-cross-origin"
                      className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-black hover:text-[#FF3B30] transition-colors border-b border-black/30 hover:border-[#FF3B30] pb-0.5"
                      data-testid={`viewer-${viewer.id}-prototype`}
                    >
                      Open Figma prototype
                      <span aria-hidden>↗</span>
                    </a>
                  </div>
                )}
              </DialogHeader>

              <div className="bg-[#eaeae3]">
                {viewer.caseStudyImages?.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${viewer.title} case study — section ${i + 1}`}
                    loading={i < 2 ? "eager" : "lazy"}
                    className="block w-full h-auto"
                    data-testid={`viewer-${viewer.id}-image-${i}`}
                  />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Projects;
